import React, { useEffect, useState } from "react";
import { Row, Col, ButtonToolbar, Button } from "reactstrap";
import { Form, Field } from "react-final-form";
import { connect } from "react-redux";
import { withRouter, useHistory } from "react-router-dom";
import { useDispatch, sendFlashMessage } from "@/shared/components/flash";
import ApiErrorMessage from "@/shared/components/ApiErrorMessage";
import axios from "axios";
import Select from "react-select";
import WYSIWYGEditor from "@/shared/components/form/WYSIWYGEditor";
import { QueryClient, QueryClientProvider, useQuery } from "react-query";
import { usePrograms } from "@/shared/hooks/usePrograms";
import { flattenProgramTree } from "@/shared/utils/programTree";

const queryClient = new QueryClient();

const FaqForm = ({ organization }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { programs: rawPrograms } = usePrograms(organization.id);

  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [source, setSource] = useState(null);

  // Prepare dropdown options
  const programOptions = [
    { value: null, label: "Global (All Programs)" },
    ...flattenProgramTree(rawPrograms),
  ];

  // Fetch FAQ
  const fetchFaq = async (values, form = null) => {
    if (!values.audience_type) return;

    try {
      setLoading(true);

      const res = await axios.get(`/organization/${organization.id}/faqs`, {
        params: {
          audience_type: values.audience_type,
          program_id: values.program_id || null,
        },
      });

      const content = res.data?.data?.content || "";
      const sourceType = res.data?.data?.source;

      setSource(sourceType);

      if (form) {
        form.change("content", content);
      } else {
        setInitialValues({
          ...values,
          content,
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchFaq({
      audience_type: "manager",
      program_id: null,
    });
  }, [organization.id]);

  // Submit
  const onSubmit = async (values) => {
    try {
      setLoading(true);

      await axios.post(`/organization/${organization.id}/faqs`, values);

      dispatch(sendFlashMessage("FAQ saved successfully", "alert-success"));
    } catch (error) {
      dispatch(
        sendFlashMessage(
          <ApiErrorMessage errors={error.response?.data} />,
          "alert-danger",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  if (!initialValues) return <p>Loading...</p>;

  return (
    <Form onSubmit={onSubmit} initialValues={initialValues}>
      {({ handleSubmit, values, form }) => (
        <form className="form" onSubmit={handleSubmit}>
          {/* Header */}
          <Row>
            <Col md="6">
              <h3>FAQ Settings</h3>
            </Col>
            <Col md="6" className="text-right">
              <ButtonToolbar>
                <Button outline onClick={() => history.goBack()}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={loading || !values.audience_type}
                >
                  Save
                </Button>
              </ButtonToolbar>
            </Col>
          </Row>

          {/* Audience */}
          <Row>
            <Col md="6">
              <Field name="audience_type">
                {({ input }) => (
                  <div className="form__form-group">
                    <span>Audience</span>
                    <Select
                      options={[
                        { value: "manager", label: "manager" },
                        { value: "participant", label: "participant" },
                      ]}
                      value={{
                        value: input.value,
                        label: input.value,
                      }}
                      onChange={(opt) => {
                        input.onChange(opt.value);
                        fetchFaq({ ...values, audience_type: opt.value }, form);
                      }}
                    />
                  </div>
                )}
              </Field>
            </Col>
          </Row>

          {/* Program */}
          <Row>
            <Col md="6">
              <Field name="program_id">
                {({ input }) => (
                  <div className="form__form-group">
                    <span>Program</span>
                    <Select
                      options={programOptions}
                      isClearable
                      value={
                        programOptions.find((o) => o.value === input.value) ||
                        null
                      }
                      onChange={(opt) => {
                        const val = opt ? opt.value : null;
                        input.onChange(val);

                        fetchFaq({ ...values, program_id: val }, form);
                      }}
                    />
                  </div>
                )}
              </Field>
            </Col>
          </Row>

          {/* Source Info */}
          <Row>
            <Col md="12">
              {source === "global" && (
                <p className="text-muted">Using Global FAQ</p>
              )}
              {source === "parent" && (
                <p className="text-warning">Inherited from Parent Program</p>
              )}
              {source === "program" && (
                <p className="text-success">Custom FAQ for this program</p>
              )}
            </Col>
          </Row>

          {/* Override Warning */}
          {(source === "global" || source === "parent") && (
            <Row>
              <Col md="12">
                <p className="text-info">
                  Saving will override FAQ for selected program
                </p>
              </Col>
            </Row>
          )}

          {/* Editor */}
          <Row>
            <Col md="12">
              <Field name="content">
                {({ input }) => (
                  <div className="form__form-group">
                    <span>FAQ Content</span>
                    <WYSIWYGEditor
                      value={input.value}
                      onChange={input.onChange}
                    />
                  </div>
                )}
              </Field>
            </Col>
          </Row>
        </form>
      )}
    </Form>
  );
};

const TableWrapper = ({ organization }) => {
  if (!organization) return "Loading...";
  return (
    <QueryClientProvider client={queryClient}>
      <FaqForm organization={organization} />
    </QueryClientProvider>
  );
};

export default withRouter(
  connect((state) => ({
    organization: state.organization,
  }))(TableWrapper),
);
